// src/pages/Referrals.js
import React, { useState, useEffect } from 'react';
import {
    Container, Row, Col, Card, Table, Badge,
    Spinner, Alert, Form, InputGroup
} from 'react-bootstrap';
import { Search } from 'react-bootstrap-icons';
import api from '../services/api';
import { useTranslation } from 'react-i18next';

const Referrals = () => {
    const [referrals, setReferrals] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [search, setSearch] = useState('');
    const [stats, setStats] = useState({
        totalReferrals: 0,
        activeReferrals: 0,
        totalLinks: 0
    });

    const { t } = useTranslation();

    useEffect(() => {
        fetchReferrals();
    }, []);

    const fetchReferrals = async () => {
        try {
            setLoading(true);
            const response = await api.get('/api/seller/referrals');
            setReferrals(response.data.data || []);

            // Подсчитываем статистику
            if (response.data.data) {
                const activeCount = response.data.data.filter(user =>
                    new Date(user.lastLogin) > new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)
                ).length;

                // Получаем уникальные ссылки
                const uniqueLinks = [...new Set(response.data.data.map(user => user.linkId))];

                setStats({
                    totalReferrals: response.data.data.length,
                    activeReferrals: activeCount,
                    totalLinks: uniqueLinks.length
                });
            }
        } catch (err) {
            setError(err.response?.data?.message || t('errors.loadingError'));
            console.error('Error fetching referrals:', err);
        } finally {
            setLoading(false);
        }
    };

    // Фильтрация рефералов
    const filteredReferrals = referrals.filter(referral =>
        referral.username.toLowerCase().includes(search.toLowerCase()) ||
        referral.linkName.toLowerCase().includes(search.toLowerCase())
    );

    // Форматирование даты
    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return new Date(dateString).toLocaleString();
    };

    return (
        <Container className="my-4">
            <h1 className="mb-4">{t('referral.myReferrals')}</h1>

            {error && (
                <Alert variant="danger" onClose={() => setError('')} dismissible>
                    {error}
                </Alert>
            )}

            <Row className="mb-4">
                <Col md={4}>
                    <Card className="stat-card card-highlight h-100">
                        <Card.Body className="text-center">
                            <h6 className="text-secondary mb-2">{t('referral.totalReferrals')}</h6>
                            <h2>{stats.totalReferrals}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="stat-card card-highlight h-100">
                        <Card.Body className="text-center">
                            <h6 className="text-secondary mb-2">{t('referral.activeReferrals')}</h6>
                            <h2>{stats.activeReferrals}</h2>
                        </Card.Body>
                    </Card>
                </Col>
                <Col md={4}>
                    <Card className="stat-card card-highlight h-100">
                        <Card.Body className="text-center">
                            <h6 className="text-secondary mb-2">{t('referral.usedLinks')}</h6>
                            <h2>{stats.totalLinks}</h2>
                        </Card.Body>
                    </Card>
                </Col>
            </Row>

            <Card className="glass-card">
                <Card.Header className="d-flex justify-content-between align-items-center">
                    <h5 className="mb-0">{t('referral.usersList')}</h5>
                    <Form.Group style={{ width: '300px' }}>
                        <InputGroup>
                            <InputGroup.Text>
                                <Search />
                            </InputGroup.Text>
                            <Form.Control
                                type="text"
                                placeholder={t('referral.searchUsers')}
                                value={search}
                                onChange={(e) => setSearch(e.target.value)}
                            />
                        </InputGroup>
                    </Form.Group>
                </Card.Header>
                <Card.Body>
                    {loading ? (
                        <div className="text-center py-5">
                            <Spinner animation="border" variant="primary" />
                            <p className="mt-3">{t('common.loading')}</p>
                        </div>
                    ) : filteredReferrals.length > 0 ? (
                        <div className="table-responsive">
                            <Table hover className="web3-table">
                                <thead>
                                <tr>
                                    <th>{t('common.user')}</th>
                                    <th>{t('referral.registrationDate')}</th>
                                    <th>{t('referral.lastLogin')}</th>
                                    <th>{t('referral.link')}</th>
                                    <th>{t('common.status')}</th>
                                </tr>
                                </thead>
                                <tbody>
                                {filteredReferrals.map(referral => {
                                    // Определяем, активен ли пользователь
                                    const isActive = new Date(referral.lastLogin) >
                                        new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);

                                    return (
                                        <tr key={referral._id}>
                                            <td><strong>{referral.username}</strong></td>
                                            <td>{formatDate(referral.createdAt)}</td>
                                            <td>{formatDate(referral.lastLogin)}</td>
                                            <td>
                                                <div className="d-flex flex-column">
                                                    <span className="mb-1">{referral.linkName}</span>
                                                    <small className="text-muted">
                                                        <code>{referral.referralCode}</code>
                                                    </small>
                                                </div>
                                            </td>
                                            <td>
                                                {isActive ? (
                                                    <Badge bg="success" pill>{t('common.active')}</Badge>
                                                ) : (
                                                    <Badge bg="secondary" pill>{t('common.inactive')}</Badge>
                                                )}
                                            </td>
                                        </tr>
                                    );
                                })}
                                </tbody>
                            </Table>
                        </div>
                    ) : (
                        <div className="text-center py-5">
                            {search ? (
                                <p>{t('referral.noSearchResults')}</p>
                            ) : (
                                <p>{t('referral.noReferrals')}</p>
                            )}
                        </div>
                    )}
                </Card.Body>
            </Card>
        </Container>
    );
};

export default Referrals;